import React, { useContext } from 'react';
import moment from 'moment';
import { DeviceEntriesContext } from '../../../__state/DeviceEntries';
import { useTheme } from '../../../__state/ThemeContext';
import { isTampered } from '../../../utils/functions';
import config from '../../../utils/config';
import __find from 'lodash/find';
import DashboardErrorBoundary from '../../../components/DashboardErrorBoundary';
import './Dashboard.scss';

const DashboardContent = ({ device }) => {
	const { theme } = useTheme();
	const selfData = JSON.parse(sessionStorage.getItem('selfData') || '{}');
	const metricValOn = selfData?.metricValOn ?? false;
	const { cachedDeviceEntries } = useContext(DeviceEntriesContext);
	const entries = cachedDeviceEntries?.[device?.id] ?? [];
	// Use the most recent entry that has METAR data (skip incomplete packets)
	const latestEntry = entries.find((e) => e?.METAR?.value) ?? entries[0] ?? null;

	// Only search for data1HourAgo if we have a valid latestEntry
	const data1HourAgo = latestEntry
		? __find(cachedDeviceEntries?.[device?.id], (entry) => {
				return moment(latestEntry.timestamp).subtract(1, 'hour').valueOf() === entry?.timestamp;
		  })
		: null;

	// Show dashboard for any device with sensor data (not just METAR)
	if (!latestEntry) {
		return (
			<div className="dashboard-loading" data-theme={theme}>
				<p>No data available for this device.</p>
			</div>
		);
	}

	const showDashboard = () => {
		// common attributes across device types - use optional chaining for safety
		const isLightningActive =
			!isNaN(latestEntry?.lightningFreq?.value) && parseInt(latestEntry?.lightningFreq?.value) > 0;
		const isRainActive =
			!isNaN(latestEntry?.precip?.value) && parseFloat(latestEntry?.precip?.value) > 0;
		const isCloudsActive =
			latestEntry?.cloudLayers?.value !== 'No Ceilometer' &&
			latestEntry?.cloudLayers?.value !== 'NCD' &&
			latestEntry?.cloudLayers?.value !== 'CLR' &&
			latestEntry?.cloudLayers?.value !== 'Ceilometer OFF';
		const isTamperingActive = isTampered(latestEntry, data1HourAgo);

		const batteryValue = parseFloat(latestEntry?.battery?.value ?? 0);
		// Battery thresholds differ by device type
		const getBatteryLevel = (value, deviceType) => {
			if (deviceType === 'IWOS') {
				// IWOS: Higher voltage range (14V system, or 36V+ if commercial power)
				if (value > 14) return 'good';    // >36V commercial power OR >14V normal battery
				if (value >= 13.5) return 'meh';  // 13.5-14V = yellow (<30%)
				return 'bad';                      // <13.5V = red (<10%)
			} else {
				// MWS: Lower voltage range (5V system)
				if (value > 5.1) return 'good';   // >5.1V = green
				if (value >= 4.8) return 'meh';   // 4.8-5V = yellow (<30%)
				return 'bad';                      // <4.8V = red (<10%)
			}
		};
		const batteryLevel = getBatteryLevel(batteryValue, device?.type);
		const showMetricOnly = metricValOn;

		const props = {
			device,
			latestEntry,
			isLightningActive,
			isRainActive,
			isCloudsActive,
			isTamperingActive,
			batteryLevel,
			showMetricOnly,
			theme,
		};

		return config[device?.type]?.['dashboard']?.(props) ?? null;
	};

	return (
		<div className="dashboard-view" data-theme={theme}>
			{showDashboard()}
		</div>
	);
};

const DashboardView = ({ device }) => {
	return (
		<DashboardErrorBoundary>
			<DashboardContent device={device} />
		</DashboardErrorBoundary>
	);
};

export default DashboardView;
